import { dayMonthMaybeYear } from "@/lib/dates";
import type { HeatmapDay, HeatmapYear } from "@/lib/stats";

import type { HoverContent } from "./HoverCard";

const SHADES = ["var(--heat-1)", "var(--heat-2)", "var(--heat-3)", "var(--heat-4)", "var(--heat-5)"];

/** Mirrors `stats/history.py`. Two negative states, and they mean opposite things about you. */
const BUCKET_MISSED = -1;
const BUCKET_IN_PROGRESS = -2;

/**
 * One day cell, in pixels.
 *
 * **8, not 9, and the difference is a scrollbar.** A full year is 53 columns — 52 weeks plus the
 * leading weekday pad — so the pitch decides whether a year fits the panel. At 9px with a 2px gap a
 * year needs 581px against the 575px the panel actually offers at the design width of 1147, and
 * those six pixels put a horizontal scrollbar under the whole grid. At 8px it needs 528 and fits
 * with 47px to spare, which survives a wider figures column too.
 *
 * The scroller stays: at 390px a year cannot fit at any legible size, and the page itself must
 * never scroll sideways.
 */
export const CELL = 8;

/**
 * A year block's exact height: seven rows and six 2px gaps.
 *
 * Exported because the year number and the year's figures live in **separate columns** from the
 * grids — the grids scroll and those two must not, or a year wide enough to need the scroller has
 * its `perfect year` clipped off the panel's right edge, which is what shipped first. Three columns
 * only line up if all three use this height and the same row gap.
 */
export const YEAR_ROW_HEIGHT = 7 * CELL + 6 * 2;

/**
 * One year of day cells: seven rows of weekdays, filling column by column.
 *
 * `grid-auto-flow: column` with seven fixed rows is the whole layout — the browser reflows the
 * weeks and nothing has to compute a week index. The first cell is offset by the year's first day
 * so the rows line up with real weekdays instead of starting wherever the data does.
 *
 * **A missed day is not the palest shade.** Paper with a red ring, because he studies nearly every
 * day: the rare blanks are the information, and a five-step ramp that quietly absorbed them would
 * make a year with eleven missed days look identical to a perfect one.
 *
 * ~2,560 cells across seven years is the largest single render in the app. Static and keyless of
 * any state, so React mounts it once; if it ever measures slow, canvas is the fallback and this
 * component is the only thing that would change.
 *
 * **The bucket is the server's, not recomputed here.** It has to be: the thresholds are quantiles
 * of the whole history's distribution, and the streak panel beside this one is derived from the
 * same series on the same server pass. A client-side re-bucketing would be a second implementation
 * of the rule, free to disagree with the legend that explains it.
 */
const WEEKDAY = new Intl.DateTimeFormat(undefined, { weekday: "short" });

const minutes = (seconds: number) =>
  seconds < 60 ? `${Math.round(seconds)}秒` : `${Math.round(seconds / 60)}分`;

/**
 * What the hover card says about one day — the design's four rows.
 *
 * Exported and pure so the wording is asserted in `Heatmap.test.ts` rather than eyeballed: the
 * three states differ in what is even *sayable*, and a missed day rendering `0.0%` and `0 / 0`
 * would be four lines claiming a measurement of nothing.
 */
export function dayHover(day: HeatmapDay): HoverContent {
  const when = new Date(`${day.day}T00:00:00`);
  const title = `${WEEKDAY.format(when)} ${dayMonthMaybeYear(when)}`;

  // The two empty states say different things and must keep doing so: `まだなし` is a day still
  // running, `なし` is a day that ended with nothing. The English pair was `none yet today` and
  // `none`, and the distinction is the whole reason this is a tested pure function.
  if (day.bucket === BUCKET_IN_PROGRESS) {
    return { title, rows: [{ label: "復習数", value: "今日はまだなし" }] };
  }
  if (day.count <= 0) return { title, rows: [{ label: "復習数", value: "なし" }] };

  return {
    title,
    rows: [
      { label: "復習数", value: day.count.toLocaleString() },
      // 未習熟 / 習熟 rather than the canvas's 若い / 熟成 — Anki's own words, and `STATE_LABEL`'s.
      { label: "未習熟 / 習熟", value: `${day.young} / ${day.mature}` },
      {
        label: "正答率",
        value: `${((100 * day.passed) / day.count).toFixed(1)}% · ${day.passed} / ${day.count}`,
      },
      { label: "時間", value: minutes(day.seconds) },
    ],
  };
}

export function YearGrid({
  year,
  onEnter,
  onLeave,
}: {
  year: HeatmapYear;
  /** From `useChartHover`. Optional so a grid can still be rendered by a test with no card. */
  onEnter?: (event: { currentTarget: Element }, content: HoverContent) => void;
  onLeave?: () => void;
}) {
  // Sunday-first, matching Anki's own calendar. `getDay()` is local time, which is what the day
  // strings are in — they were built from the server's `date`, not from an instant.
  const leading = new Date(`${year.days[0]?.day}T00:00:00`).getDay();

  return (
    <div
      className="grid grid-flow-col gap-[2px]"
      style={{ gridTemplateRows: `repeat(7, ${CELL}px)` }}
      role="img"
      aria-label={`${year.year}年: 学習${year.days.length - year.missed}日、未実施${year.missed}日`}
    >
      {Array.from({ length: leading }, (_, i) => (
        <span key={`pad-${i}`} aria-hidden style={{ height: CELL, width: CELL }} />
      ))}
      {year.days.map((day) => (
        <span
          key={day.day}
          // **The two ringed states are CLASSES now, not inline styles**, so `.chart-mark:hover`
          // can put the design's outline on them — an inline `box-shadow` beats every utility and
          // would have left a missed day and today as the only two cells that could not be
          // pointed at. The shade stays inline: it is data, not a state.
          className={`chart-mark rounded-[var(--r-2)] ${
            day.bucket === BUCKET_IN_PROGRESS
              ? "heat-today"
              : day.bucket === BUCKET_MISSED
                ? "heat-missed"
                : ""
          }`}
          style={{
            height: CELL,
            width: CELL,
            ...(day.bucket >= 0
              ? { background: SHADES[Math.min(day.bucket, SHADES.length - 1)] }
              : {}),
          }}
          onMouseEnter={onEnter && ((event) => onEnter(event, dayHover(day)))}
          onMouseLeave={onLeave}
        />
      ))}
    </div>
  );
}

/** The shade ramp, plus the two ringed states it is deliberately not part of. */
export function HeatmapLegend({
  thresholds,
  inProgress = false,
}: {
  thresholds: number[];
  /** Only named when today actually is in that state. A legend advertising a swatch that appears
   *  nowhere on the grid is a puzzle rather than a key. */
  inProgress?: boolean;
}) {
  return (
    <div className="flex items-center gap-[10px]">
      {inProgress && (
        <span className="flex items-center gap-[3px] text-meta text-ink-4">
          <span
            aria-hidden
            className="h-[11px] w-[11px] rounded-[var(--r-2)]"
            style={{
              background: "var(--heat-today-bg)",
              boxShadow: "inset 0 0 0 1.5px var(--heat-today-ring)",
            }}
          />
          今日 · 進行中
        </span>
      )}
      <span className="flex items-center gap-[3px] text-meta text-ink-4">
        <span
          aria-hidden
          className="h-[11px] w-[11px] rounded-[var(--r-2)]"
          style={{
            background: "var(--heat-missed)",
            boxShadow: "inset 0 0 0 1.5px var(--heat-missed)",
          }}
        />
        未実施
      </span>
      <span aria-hidden className="h-[10px] w-px bg-line" />
      <span className="flex items-center gap-[3px] text-meta text-ink-4">
        少
        {SHADES.map((shade, index) => (
          <span
            key={shade}
            // The threshold is what the shade *means*, so it is worth being able to point at.
            title={`${thresholds[index]?.toLocaleString() ?? ""}回以上`}
            aria-hidden
            className="h-[11px] w-[11px] rounded-[var(--r-2)]"
            style={{ background: shade }}
          />
        ))}
        多
      </span>
    </div>
  );
}
